import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";

const NavigationContext = createContext();

const STORAGE_KEY = "nav-active-panel"; // "", "before", "during", "after"

export function NavigationProvider({ children }) {
  const [roadmapOpen, setRoadmapOpen] = useState(false);
  const [checklistsOpen, setChecklistsOpen] = useState(false);
  const [activePanel, setActivePanel] = useState(""); // "before" | "during" | "after" | ""
  const [sitRepPanelIndex, setSitRepPanelIndex] = useState(0);
  const [openHub, setOpenHub] = useState(false);
  const [openTimeline, setTimelineOpen] = useState(false);
  const [openHubFlag, setOpenHubFlag] = useState(false);

  // Global weather alert sidebar state
  const [weatherAlertSidebarOpen, setWeatherAlertSidebarOpen] =
    useState(false);
  const [weatherAlertForSidebar, setWeatherAlertForSidebar] =
    useState(null);

  // Before highlights
  const [highlightChecklist, setHighlightChecklist] = useState(false);
  const [highlightRoadmap, setHighlightRoadmap] = useState(false);
  const [highlightQuickTips, setHighlightQuickTips] = useState(false);

  // "During" hub nav highlight
  const [highlightHubNavIndex, setHighlightHubNavIndex] =
    useState(null);

  // Before scroll/centering
  const [roadmapCenterRequested, setRoadmapCenterRequested] =
    useState(false);
  const [beforeScrollRequested, setBeforeScrollRequested] =
    useState(false);
  const [quickTipsCenterRequested, setQuickTipsCenterRequested] =
    useState(false);
  const [lastBeforeTarget, setLastBeforeTarget] = useState(null);

  // After-specific state
  const [highlightAfterTimeline, setHighlightAfterTimeline] =
    useState(false);
  const [highlightAfterTips, setHighlightAfterTips] = useState(false);
  const [highlightAfterChecklists, setHighlightAfterChecklists] =
    useState(false);
  const [lastAfterTarget, setLastAfterTarget] = useState(null);
  const [afterTargetPanelId, setAfterTargetPanelId] = useState(null);

  // Rehydrate from localStorage on first mount (browser only)
  useEffect(() => {
    if (typeof window === "undefined") return;
    const saved = window.localStorage.getItem(STORAGE_KEY) || "";

    setRoadmapOpen(false);
    setOpenHub(false);
    setTimelineOpen(false);

    setActivePanel(saved);

    if (saved === "before") {
      setRoadmapOpen(true);
    } else if (saved === "during") {
      setOpenHub(true);
    } else if (saved === "after") {
      setTimelineOpen(true);
    }
  }, []);

  // Persist activePanel
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(STORAGE_KEY, activePanel);
  }, [activePanel]);

  const trackNavigation = useCallback((eventName, params = {}) => {
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("event", eventName, {
        event_category: "Navigation",
        ...params,
      });
    }
    if (process.env.NODE_ENV === "development") {
      console.log("GA event:", eventName, params);
    }
  }, []);

  // Plain Before opener for parent nav; no highlights or centering
const openBeforePlain = (source = "unknown") => {
  setRoadmapOpen(true);
  setOpenHub(false);
  setTimelineOpen(false);
  setActivePanel("before");
  // Do NOT set beforeScrollRequested / roadmapCenterRequested / quickTipsCenterRequested
  // Do NOT set lastBeforeTarget here
  trackNavigation("open_before_plain", { source });
};


  // Readiness Roadmap (Before, roadmap-focused, WITH highlight)
const openRoadmap = (source = "unknown") => {
  setRoadmapOpen(true);
  setOpenHub(false);
  setTimelineOpen(false);
  setActivePanel("before");
  setBeforeScrollRequested(true);
  setRoadmapCenterRequested(true);
  setHighlightRoadmap(true);
  setLastBeforeTarget("readiness-roadmap");
  trackNavigation("open_roadmap", { source });
};

const openBeforeGeneric = (source = "unknown") => {
  setRoadmapOpen(true);
  setOpenHub(false);
  setTimelineOpen(false);
  setActivePanel("before");
  setBeforeScrollRequested(true);
  setLastBeforeTarget("quick-safety-tips-and-tools");
  setHighlightQuickTips(true);
  setQuickTipsCenterRequested(true);
  trackNavigation("open_before_generic", { source });
};

const openBeforeChecklists = (source = "unknown") => {
  setRoadmapOpen(true);
  setOpenHub(false);
  setTimelineOpen(false);
  setActivePanel("before");
  setChecklistsOpen(true);
  setHighlightChecklist(true);
  setBeforeScrollRequested(true);
  setLastBeforeTarget("preparedness-checklists");
  trackNavigation("open_before_checklists", { source });
};


  const openEmergencyHub = (source = "unknown") => {
    setOpenHub(true);
    setRoadmapOpen(false);
    setTimelineOpen(false);
    setActivePanel("during");
    trackNavigation("open_hub", { source });
  };

  // After: main top-level (Recovery Timeline section)
  const openTimelinePanel = (source = "unknown") => {
    setTimelineOpen(true);
    setRoadmapOpen(false);
    setOpenHub(false);
    setActivePanel("after");
    setLastAfterTarget("recovery-timeline");

    setAfterTargetPanelId(null);
    setTimeout(() => {
      setAfterTargetPanelId("recovery-timeline");
    }, 0);

    trackNavigation("open_timeline", { source });
  };

  // After: Mental Health
  const openAfterMentalHealth = (source = "unknown") => {
    setTimelineOpen(true);
    setRoadmapOpen(false);
    setOpenHub(false);
    setActivePanel("after");
    setHighlightAfterTips(true);
    setLastAfterTarget("mental-health-resources");

    setAfterTargetPanelId(null);
    setTimeout(() => {
      setAfterTargetPanelId("mental-health-resources");
    }, 0);

    trackNavigation("open_after_mental_health", { source });
  };

  // After: Timeline (explicit deep link)
  const openAfterTimeline = (source = "unknown") => {
    setTimelineOpen(true);
    setRoadmapOpen(false);
    setOpenHub(false);
    setActivePanel("after");
    setHighlightAfterTimeline(true);
    setLastAfterTarget("recovery-timeline");

    setAfterTargetPanelId(null);
    setTimeout(() => {
      setAfterTargetPanelId("recovery-timeline");
    }, 0);

    trackNavigation("open_after_timeline", { source });
  };

  // After: Checklists
  const openAfterChecklists = (source = "unknown") => {
    setTimelineOpen(true);
    setRoadmapOpen(false);
    setOpenHub(false);
    setActivePanel("after");
    setHighlightAfterChecklists(true);
    setLastAfterTarget("recovery-checklists");

    setAfterTargetPanelId(null);
    setTimeout(() => {
      setAfterTargetPanelId("recovery-checklists");
    }, 0);

    trackNavigation("open_after_checklists", { source });
  };

  const openWeatherAlertSidebar = (alert, source = "unknown") => {
    setWeatherAlertForSidebar(alert);
    setWeatherAlertSidebarOpen(true);
    trackNavigation("open_weather_alert_sidebar", {
      source,
      event: alert?.properties?.event,
      severity: alert?.properties?.severity,
    });
  };

  const closeWeatherAlertSidebar = (source = "unknown") => {
    setWeatherAlertSidebarOpen(false);
    trackNavigation("close_weather_alert_sidebar", { source });
  };

  const closePanels = () => {
    setRoadmapOpen(false);
    setChecklistsOpen(false);
    setOpenHub(false);
    setTimelineOpen(false);
    setActivePanel("");
    setWeatherAlertSidebarOpen(false);
    setWeatherAlertForSidebar(null);
    setHighlightChecklist(false);
    setHighlightRoadmap(false);
    setHighlightQuickTips(false);
    setHighlightHubNavIndex(null);
    setRoadmapCenterRequested(false);
    setBeforeScrollRequested(false);
    setQuickTipsCenterRequested(false);
    setHighlightAfterTips(false);
    setHighlightAfterTimeline(false);
    setHighlightAfterChecklists(false);
    setAfterTargetPanelId(null);
  };

  const value = {
    roadmapOpen,
    setRoadmapOpen,
    checklistsOpen,
    setChecklistsOpen,
    activePanel,
    setActivePanel,
    sitRepPanelIndex,
    setSitRepPanelIndex,
    openHub,
    setOpenHub,
    openTimeline,
    setTimelineOpen,
    openHubFlag,
    setOpenHubFlag,

    // Before
    openBeforePlain,
    openRoadmap,
    openBeforeGeneric,
    openBeforeChecklists,

    // During
    openEmergencyHub,

    // Weather
    weatherAlertSidebarOpen,
    weatherAlertForSidebar,
    openWeatherAlertSidebar,
    closeWeatherAlertSidebar,

    // Before highlights
    highlightChecklist,
    setHighlightChecklist,
    highlightRoadmap,
    setHighlightRoadmap,
    highlightQuickTips,
    setHighlightQuickTips,

    // Hub highlights
    highlightHubNavIndex,
    setHighlightHubNavIndex,

    // Before scroll / state
    closePanels,
    trackNavigation,
    roadmapCenterRequested,
    setRoadmapCenterRequested,
    beforeScrollRequested,
    setBeforeScrollRequested,
    quickTipsCenterRequested,
    setQuickTipsCenterRequested,
    lastBeforeTarget,
    setLastBeforeTarget,

    // After
    highlightAfterTimeline,
    setHighlightAfterTimeline,
    highlightAfterTips,
    setHighlightAfterTips,
    highlightAfterChecklists,
    setHighlightAfterChecklists,
    lastAfterTarget,
    setLastAfterTarget,
    afterTargetPanelId,
    setAfterTargetPanelId,
    openAfterMentalHealth,
    openAfterTimeline,
    openAfterChecklists,
    openTimelinePanel,
  };

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
}

export function useNavigationContext() {
  return useContext(NavigationContext);
}
